/**
 * Live preview of the projected `Both` row, computed by the API from the
 * Front/Back rules currently in the editor. Hidden when the API responds
 * with `503` (the auto-compose feature flag is off).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useWatch } from 'react-hook-form';
import {
  Box, Typography, Chip, Collapse, Alert, IconButton, Skeleton, Stack, Tooltip,
} from '@mui/material';
import { ExpandMoreRounded as ExpandMore, ExpandLessRounded as ExpandLess } from '@mui/icons-material';
import { httpClient, HttpError } from '@/utils/httpClient';
import { DOCUMENT_SIDE_TO_INT } from '@/config/constants';
import type { BothPreview, KycMappingFieldDto } from './bothComposition.types';
import { RuleSummary } from './RuleSummary';

export interface PreviewHypothesis {
  documentTypeId?: string | number | null;
  nationalityId?: string | number | null;
  /** Backend integer enum: 1=Front, 2=Back. Both is intentionally not allowed here. */
  documentSide: number;
  mappingFields: Array<{
    fieldName: string;
    regex: string;
    kycMappingField: KycMappingFieldDto[];
  }>;
}

interface Props {
  hypothesis: PreviewHypothesis;
  /** Look up KYC field display name by id. */
  kycFieldNameById?: Record<string, string>;
}

const DEBOUNCE_MS = 500;

const isFormReady = (h: PreviewHypothesis) =>
  Boolean(h.documentTypeId) &&
  Boolean(h.nationalityId) &&
  (h.documentSide === 1 || h.documentSide === 2) &&
  (h.mappingFields || []).some(
    (f) => (f.fieldName || '').trim() && (f.kycMappingField || []).some((k) => k.kycFieldId || k.prefix),
  );

export const BothPreviewPanel: React.FC<Props> = ({ hypothesis, kycFieldNameById }) => {
  const t = useTranslate();
  const [open, setOpen] = useState(true);
  const [preview, setPreview] = useState<BothPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Stringify the payload so the effect re-runs only when the relevant data changes.
  const key = useMemo(() => JSON.stringify(hypothesis), [hypothesis]);
  const ready = isFormReady(hypothesis);

  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (hidden) return;
    if (!ready) {
      setPreview(null);
      return;
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await httpClient.post<BothPreview>(
          '/admin/fields-mapping/PreviewBoth',
          hypothesis,
        );
        setPreview(r.data);
      } catch (e) {
        if (e instanceof HttpError && e.status === 503) {
          // Feature flag is off — hide the panel for the rest of this session.
          setHidden(true);
          setPreview(null);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hidden, ready]);

  if (hidden) return null;
  if (!ready) return null;

  const conflicts = preview?.unresolvedConflicts || [];
  const rules = preview?.toPersist || [];
  const toggleLabel = t(open ? 'ra.action.close' : 'ra.action.expand');

  const renderSourceName = (k: KycMappingFieldDto) =>
    (k.kycFieldId && kycFieldNameById?.[k.kycFieldId]) || (k.prefix ? `"${k.prefix}"` : '—');

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" component="h3">
          {t('fields-mapping.both.preview.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loading
            ? t('fields-mapping.both.preview.loading')
            : t('screens.fieldMappings.editor.ruleCount', { smart_count: rules.length })}
        </Typography>
        {conflicts.length > 0 && (
          <Chip size="small" color="warning" label={t('screens.fieldMappings.conflictCount', { smart_count: conflicts.length })} />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={toggleLabel}>
          <IconButton onClick={() => setOpen((o) => !o)} aria-label={toggleLabel} aria-expanded={open}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {loading && !preview && (
            <Box>
              <Skeleton height={26} />
              <Skeleton height={26} />
              <Skeleton height={26} width="70%" />
            </Box>
          )}

          {!loading && rules.length === 0 && conflicts.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('fields-mapping.both.preview.empty')}
            </Typography>
          )}

          {conflicts.length > 0 && (
            <Alert severity="warning">
              {t('fields-mapping.both.preview.conflicts_hint')}{' '}
              {conflicts.map((c) => c.fieldName).join(', ')}
            </Alert>
          )}

          {rules.length > 0 && (
            <Box component="ol" sx={{ m: 0, pl: 3 }}>
              {rules.map((r, i) => (
                <Typography component="li" variant="body2" key={`${r.fieldName}-${i}`} value={r.position + 1}>
                  <RuleSummary output={r.fieldName} sources={(r.kycFields || []).map(renderSourceName)} regex={r.regex} />
                </Typography>
              ))}
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

/**
 * Feeds the preview from the form it sits in (so {@link useWatch} can follow
 * the document fields) and the rules held beside the form. Create and edit
 * name the document fields differently, hence the two source paths.
 */
export const BothPreviewWatcher: React.FC<{
  mappingFields: Array<{ standardFieldName?: string; regex?: string; kycMappingField?: Array<{ kycField?: { id?: number | string } | null; prefix?: string; suffix?: string }> }>;
  kycFieldNameById: Record<string, string>;
  documentTypeSource: string;
  nationalitySource: string;
}> = ({ mappingFields, kycFieldNameById, documentTypeSource, nationalitySource }) => {
  const documentTypeId = useWatch({ name: documentTypeSource });
  const nationalityId = useWatch({ name: nationalitySource });
  const documentSide = useWatch({ name: 'documentSide' });
  const sideInt = typeof documentSide === 'number' ? documentSide : (DOCUMENT_SIDE_TO_INT[documentSide] ?? 0);
  return (
    <BothPreviewPanel
      kycFieldNameById={kycFieldNameById}
      hypothesis={{
        documentTypeId,
        nationalityId,
        documentSide: sideInt,
        mappingFields: mappingFields.map((f) => ({
          fieldName: f.standardFieldName || '',
          regex: f.regex || '',
          kycMappingField: (f.kycMappingField || []).map((k) => ({
            kycFieldId: (k.kycField?.id || null) as string | null,
            prefix: k.prefix || '',
            suffix: k.suffix || '',
          })),
        })),
      }}
    />
  );
};
